import * as d3 from 'd3';
import { BarChartConfig } from '../data/BarChartConfig';
import { ChartDataType } from '../data/ChartDataType';
import { BandAxisRenderer } from './BandAxisRenderer';
import { ChartRendererContext } from './ChartRendererContext';
import { ColumnBarChartRenderer } from './ColumnBarChartRenderer';
import { LegendRenderer } from './LegendRenderer';
import { LinearAxisRenderer } from './LineAxisChartRenderer';
import { LineRenderer } from './LineRenderer';
import { Rectangle } from './Rectange';
import { TooltipRenderer } from './TooltipRenderer';
import { ColumnRenderer } from './ColumnRenderer';
import { ParallelColumnRenderer } from './ParallelColumnRenderer';
import { StackedColumnRenderer } from './StackedColumnRenderer';
import { DataRenderer } from './DataRenderer';
import { HighlightRenderer } from './HighlightRenderer';

export class ColumnChartRenderer extends ColumnBarChartRenderer {
  constructor(protected config: BarChartConfig) {
    super(config);
  }

  render(el: HTMLElement, context: ChartRendererContext) {
    // change this later when mnb-chart passes in ele[0] instead of ele
    const temp: any = el;
    const ele: HTMLElement = temp.length ? temp[0] : temp;
    this.ele = ele;

    this.svgElement = this.createSvgElement(ele);
    this.svgHTMLElement = this.svgElement.node()!;

    let legendHeight = this.config.legendConfig.hide ? 5 : 20;

    const dimensions: { height: number; width: number } = ColumnBarChartRenderer.getDimensions(ele);

    if (!this.config.legendConfig.hide) {
      legendHeight = new LegendRenderer(context, this.config).render(this.svgElement, this.config.data!, dimensions);
    }

    if (!legendHeight) {
      legendHeight = 5;
    }

    const chartRect: Rectangle = {
      x: 0,
      y: 5,
      width: dimensions.width - 1,
      height: dimensions.height - legendHeight - 5,
    };

    this.gElement = this.svgElement.append('g');

    const yAxisRenderer = new LinearAxisRenderer(
      this.gElement,
      context,
      this.config,
      false,
      this.rendererConfig.hasSecondaryAxis()
    );
    const xAxisRenderer = new BandAxisRenderer(this.gElement, context, this.config, true);
    const dataLimit = this.config.limitXAxis
      ? xAxisRenderer.calcMaximumBands(
          chartRect.width -
            (!this.config.yAxisConfig.hide ? 25 + (this.rendererConfig.hasSecondaryAxis() ? 25 : 0) : 0),
          this.rendererConfig
        )
      : 100000;

    const xAxisHeight = xAxisRenderer.calcXHeight();
    const innerHeight = chartRect.height - xAxisHeight;

    const yAxis = yAxisRenderer.renderY(chartRect.width, innerHeight, dataLimit);

    let secondaryYAxis:
      | {
          scale: d3.ScaleLinear<number, number>;
          width: number;
        }
      | undefined = undefined;

    if (this.rendererConfig.hasSecondaryAxis()) {
      const secondaryYAxisRenderer = new LinearAxisRenderer(this.gElement, context, this.config, true, true);
      secondaryYAxis = secondaryYAxisRenderer.renderY(chartRect.width - yAxis.width, innerHeight, dataLimit);
    }

    const innerRect: Rectangle = {
      x: chartRect.x + yAxis.width,
      y: chartRect.y,
      width: chartRect.width - yAxis.width - (secondaryYAxis ? secondaryYAxis.width : 0),
      height: innerHeight,
    };

    this.gElement.attr('transform', `translate(${innerRect.x},${innerRect.y})`);

    const xAxis = xAxisRenderer.renderX(innerRect.width, innerRect.height, dataLimit);

    const dataRenderers: DataRenderer[] = [];

    const highlightRenderer = new HighlightRenderer(this.gElement, false, this.config.highlightLineColor, true);

    highlightRenderer.render(xAxis.scale, yAxis.scale);
    dataRenderers.push(highlightRenderer);

    const columnRenderers: ColumnRenderer[] = [];
    const lineRenderers: LineRenderer[] = [];

    const secondaryAsLine = this.config.secondaryAsLine;

    // comparison series
    if (this.rendererConfig.hasComparisonData()) {
      // primary
      columnRenderers.push(
        new ColumnRenderer(
          this.gElement,
          this.rendererConfig.getComparisonData()!,
          this.rendererConfig.hasComparisonData(),
          this.rendererConfig.hasSecondaryData() && !secondaryAsLine
        )
      );
      if (this.rendererConfig.hasSecondaryData() && !secondaryAsLine) {
        columnRenderers.push(
          new ColumnRenderer(
            this.gElement,
            this.rendererConfig.getSecondaryComparisonData()!,
            this.rendererConfig.hasComparisonData(),
            this.rendererConfig.hasSecondaryData(),
            this.rendererConfig.hasSecondaryAxis()
          )
        );
      }
    }

    if (this.config.breakdownMode) {
      // render with breakdown data. breakdown doesn't render any comparison
      switch (this.config.breakdownMode) {
        case 'stacked':
          const stackedRenderer = new StackedColumnRenderer(this.gElement, false);
          stackedRenderer.render(
            this.config.getDataArrays(ChartDataType.breakdown),
            xAxis.scale,
            yAxis.scale,
            dataLimit
          );
          break;
        case 'fill-stacked':
          const fillStackedRenderer = new StackedColumnRenderer(this.gElement, true);
          fillStackedRenderer.render(
            this.config.getDataArrays(ChartDataType.breakdown),
            xAxis.scale,
            yAxis.scale,
            dataLimit
          );
          break;
        case 'parallel':
          const parallelRenderer = new ParallelColumnRenderer(this.gElement);
          parallelRenderer.render(this.config.data!, xAxis.scale, yAxis.scale);
          break;
        default:
          console.error('breakdownData without a configured breakdownMode');
      }
    } else {
      // primary
      columnRenderers.push(
        new ColumnRenderer(
          this.gElement,
          this.rendererConfig.getPrimaryData()!,
          this.rendererConfig.hasComparisonData(),
          this.rendererConfig.hasSecondaryData() && !secondaryAsLine
        )
      );
    }
    if (this.rendererConfig.hasSecondaryData()) {
      if (!secondaryAsLine) {
        columnRenderers.push(
          new ColumnRenderer(
            this.gElement,
            this.rendererConfig.getSecondaryData()!,
            this.rendererConfig.hasComparisonData(),
            this.rendererConfig.hasSecondaryData(),
            this.rendererConfig.hasSecondaryAxis()
          )
        );
      } else {
        lineRenderers.push(
          new LineRenderer(
            this.gElement,
            this.rendererConfig.getSecondaryData()!,
            undefined,
            this.rendererConfig.hasSecondaryAxis()
          )
        );
        if (this.rendererConfig.hasComparisonData()) {
          lineRenderers.push(
            new LineRenderer(
              this.gElement,
              this.rendererConfig.getSecondaryComparisonData()!,
              undefined,
              this.rendererConfig.hasSecondaryAxis()
            )
          );
        }
      }
    }

    if (!this.config.breakdownMode) {
      if (this.rendererConfig.hasPlanData()) {
        columnRenderers.push(
          new ColumnRenderer(
            this.gElement,
            this.rendererConfig.getPrimaryPlanData()!,
            this.rendererConfig.hasComparisonData(),
            this.rendererConfig.hasSecondaryData(),
            undefined,
            secondaryAsLine
          )
        );
      }
      if (this.rendererConfig.hasSecondaryData() && this.rendererConfig.getSecondaryPlanData() && !secondaryAsLine) {
        columnRenderers.push(
          new ColumnRenderer(
            this.gElement,
            this.rendererConfig.getSecondaryPlanData()!,
            this.rendererConfig.hasComparisonData(),
            this.rendererConfig.hasSecondaryData(),
            this.rendererConfig.hasSecondaryAxis()
          )
        );
      } else if (this.rendererConfig.hasSecondaryData() && this.rendererConfig.getSecondaryPlanData()) {
        lineRenderers.push(
          new LineRenderer(
            this.gElement,
            this.rendererConfig.getSecondaryPlanData()!,
            undefined,
            this.rendererConfig.hasSecondaryAxis()
          )
        );
      }
    }

    new TooltipRenderer(this.config, this.tooltipContent!, [
      ...columnRenderers,
      ...lineRenderers,
      ...dataRenderers,
    ]).render(
      this.svgElement,
      innerRect,
      xAxis.scale,
      yAxis.scale,
      dataLimit,
      false,
      false,
      secondaryYAxis ? secondaryYAxis.scale : undefined
    );

    columnRenderers.forEach(column =>
      column.render(xAxis.scale, !column.secondaryAxis ? yAxis.scale : secondaryYAxis!.scale, dataLimit)
    );

    lineRenderers.forEach(line =>
      line.render(xAxis.scale, !line.secondaryAxis ? yAxis.scale : secondaryYAxis!.scale, dataLimit)
    );
    lineRenderers.forEach(line =>
      line.renderGuides(xAxis.scale, !line.secondaryAxis ? yAxis.scale : secondaryYAxis!.scale, dataLimit)
    );
  }
}
